import { ArrowUpRight, Instagram, Linkedin, Mail, MapPin, MessageCircleMore, ReceiptText, Phone } from "lucide-react";
import { Link } from "react-router-dom";
import { footerDetails } from "@/components/equinox/data";

const footerItems = [
  { label: "Instagram", value: footerDetails.instagram, icon: Instagram, href: footerDetails.instagramHref, external: true, glow: "from-white/12 to-blue-500/12" },
  { label: "LinkedIn", value: footerDetails.linkedin, icon: Linkedin, href: footerDetails.linkedinHref, external: true, glow: "from-blue-500/16 to-white/10" },
  { label: "Address", value: footerDetails.address, icon: MapPin, glow: "from-white/12 to-slate-500/12" },
  { label: "GSTIN", value: footerDetails.gstin, icon: ReceiptText, glow: "from-amber-300/16 to-white/10" },
  { label: "WhatsApp", value: footerDetails.whatsapp, icon: MessageCircleMore, href: footerDetails.whatsappHref, external: true, glow: "from-emerald-400/16 to-white/10" },
  { label: "Email", value: footerDetails.email, icon: Mail, href: `mailto:${footerDetails.email}`, external: true, glow: "from-white/12 to-blue-500/14" },
];

const legalLinks = [
  { label: "Terms & Conditions", href: "/legal/terms-and-conditions" },
  { label: "Privacy Policy", href: "/legal/privacy-policy" },
  { label: "Returns & Refunds", href: "/legal/returns-and-refunds" },
];

export default function SiteFooter({ ctaLabel, onPrimaryAction }) {
  return (
    <footer className="px-6 pb-14 pt-12 sm:px-8 lg:px-12">
      <div className="premium-footer-shell mx-auto max-w-7xl overflow-hidden rounded-[2.4rem] border border-white/[0.08] p-6 backdrop-blur-2xl lg:p-8">
        <div className="mb-8 h-px w-full bg-gradient-to-r from-transparent via-white/45 to-transparent" />
        <div className="grid gap-8 lg:grid-cols-[1.05fr_0.95fr]">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-white/[0.4]">Equinox Marketing Agency</p>
            <h2 className="mt-4 max-w-[14ch] font-display text-3xl text-white sm:text-4xl">AI-powered growth systems for brands that want more than campaigns.</h2>
            <p className="mt-4 max-w-xl text-sm leading-7 text-white/[0.62]">
              Premium growth infrastructure for apps and brands — combining ASO, performance marketing, automation, and intelligent systems.
            </p>
            <div className="mt-5 flex flex-wrap gap-2" data-testid="footer-credibility-strip">
              <span className="rounded-full border border-white/[0.08] bg-white/[0.04] px-3 py-2 text-[11px] uppercase tracking-[0.22em] text-white/[0.52]">Founder-led</span>
              <span className="rounded-full border border-white/[0.08] bg-white/[0.04] px-3 py-2 text-[11px] uppercase tracking-[0.22em] text-white/[0.52]">Operator-built systems</span>
              <span className="rounded-full border border-white/[0.08] bg-white/[0.04] px-3 py-2 text-[11px] uppercase tracking-[0.22em] text-white/[0.52]">Apps + brands</span>
            </div>
            <div className="mt-6 flex flex-wrap gap-4 text-sm text-white/[0.72]">
              <a href={`mailto:${footerDetails.email}`} className="inline-flex items-center gap-2" data-testid="footer-email-link">
                <Mail className="h-4 w-4 text-blue-200" />
                {footerDetails.email}
              </a>
              <a href={`tel:${footerDetails.phone}`} className="inline-flex items-center gap-2" data-testid="footer-phone-link">
                <Phone className="h-4 w-4 text-white/80" />
                {footerDetails.phone}
              </a>
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            {footerItems.map(({ label, value, icon: Icon, href, external, glow }) => {
              const content = (
                <div className="control-room-panel glitter-card cinematic-card rounded-[1.5rem] p-4 transition-transform duration-300 hover:scale-[1.02]" data-testid={`footer-item-${label.toLowerCase()}`}>
                  <div className={`mb-3 inline-flex h-11 w-11 items-center justify-center rounded-2xl border border-white/[0.12] bg-gradient-to-br ${glow} text-white shadow-[0_0_28px_rgba(255,255,255,0.08)]`}>
                    <Icon className="h-5 w-5" />
                  </div>
                  <p className="text-xs uppercase tracking-[0.22em] text-white/[0.42]">{label}</p>
                  <p className="mt-2 text-sm leading-6 text-white/[0.72]">{value}</p>
                </div>
              );

              if (href) {
                return external ? (
                  <a key={label} href={href} target="_blank" rel="noreferrer" aria-label={label}>
                    {content}
                  </a>
                ) : (
                  <Link key={label} to={href} aria-label={label}>
                    {content}
                  </Link>
                );
              }

              return <div key={label}>{content}</div>;
            })}
          </div>
        </div>

        <div className="mt-8 flex flex-col gap-4 border-t border-white/[0.08] pt-6 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-sm text-white/[0.48]">All rights reserved · Equinox Marketing Agency · Since 2019</p>
            <div className="mt-3 flex flex-wrap gap-x-4 gap-y-2">
              {legalLinks.map((item) => (
                <Link
                  key={item.href}
                  to={item.href}
                  className="text-sm text-white/[0.58] transition-colors hover:text-white"
                  data-testid={`footer-legal-link-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                >
                  {item.label}
                </Link>
              ))}
            </div>
          </div>
          <button
            type="button"
            onClick={onPrimaryAction}
            className="glass-button rounded-full bg-white px-5 py-3 text-sm font-medium text-black transition-transform duration-300 hover:scale-105"
            data-testid="footer-primary-cta-button"
          >
            <span className="relative z-10 inline-flex items-center gap-2">
              {ctaLabel}
              <ArrowUpRight className="h-4 w-4" />
            </span>
          </button>
        </div>
      </div>
    </footer>
  );
}